/**
 * 회원 액션 팝오버 — $j.F.Admin.Member.Pop
 *
 * 관리자 어디서든 **회원번호 하나만 있으면** 그 회원의 요약과 갈 수 있는 곳을 한 자리에 편다.
 * 기사관리 목록에서 시작했지만 오더관리·입출금·상담처럼 회원번호가 나오는 화면이면 다 쓴다.
 *
 * 쓰는 법
 *   // 1) 행 데이터가 이미 있으면 그대로 넘긴다 (요청 0회 — 기사관리 목록)
 *   $j.F.Admin.Member.Pop.open(anchorEl, row);
 *
 *   // 2) 회원번호만 있으면 서버에서 한 벌 받아 연다
 *   $j.F.Admin.Member.Pop.openByNo(anchorEl, 21429);
 *
 *   // 3) 위임 — 선택자 안의 [data-mb-pop] 을 누르면 알아서 열린다
 *   $j.F.Admin.Member.Pop.bind('#table');
 *   <a href="javascript:void(0)" data-mb-pop="21429">21429</a>
 *
 * 왜 Ui.Tooltip 위에 얹었나 — 앵커 기준 팝오버가 필요한데 그쪽이 이미 body 에 붙고
 * (표의 overflow 에 안 잘린다), 같은 앵커를 다시 누르면 닫히고, 바깥 클릭도 처리한다.
 *
 * 값 계산(msixPt/counterOf/sooCnt/cashLeft)은 여기 있고 회원목록 표(ListTable)도 이걸 쓴다.
 * 두 벌로 두면 같은 회원인데 표와 팝오버가 다른 숫자를 말하게 된다.
 *
 * 서버 한 벌: GET /adm/ajax/member/{mb_no}/pop (Admin\MemberController@pop)
 *   — 목록 SELECT 와 같은 자리에서 뽑는다.
 *
 * 렌더가 쓰는 것: $j.F.Admin.Util (memberPortal/chat/userOrder/memberDocEdit/userDocs/userCash 등)
 *   · Util(import)
 */
import Util from '../../../Common/Util';
import LayoutData from '../LayoutData';

export default class Pop {

    /* ── 값 계산 ────────────────────────────────────────────────
       컬럼 렌더와 액션 팝오버가 **같은 함수**를 본다. 두 벌로 두면 같은 회원인데
       표와 팝오버가 다른 숫자를 말하게 된다. */

    /** -6차 잔량. 수발주자 소유자만 갖는다 (소속자는 소유자 자원과 동일해 표시 생략) */
    static msixPt(row) {
        if (row.mem_driver_type !== '수발주자') return 0;
        if (row.mem_permit === '소속자') return 0;
        return Math.max(0, Util.int(row.msix_pt_sum));
    }

    /** 발주차수. -6차가 남아 있으면 -6 이 우선한다 */
    static counterOf(row) {
        return Util.int(row.msix_pt_sum) > 0 ? -6 : Util.int(row.mem_ordercounter);
    }

    /** 이번달 수주건수 */
    static sooCnt(row) {
        return Util.int(row.soo_cnt1) + Util.int(row.soo_cnt2);
    }

    /** 가용캐시 (보유 - 수주홀드). 소유자만 갖는다 */
    static cashLeft(row) {
        return row.mem_permit === '소유자' ? (Util.int(row.mem_mycashe) - Util.int(row.soo_hold)) : null;
    }

    /**
     * '최신' 판정 기준이 되는 앱버전 (g5_apps 의 JB latest_ver · 아이폰 회원은 ios_latest_ver).
     *
     * @param {object} [row] 회원 행. mobile_type 이 ios 면 아이폰 기준(미설정이면 0 = 기준 모름)
     *
     * 기사관리는 제 화면에서 window.appVer 를 심고, 그 밖의 화면은 안 심는다 — 대신 관리자
     * 관리자 레이아웃이 헤더 버전표시용으로 늘 넘긴다(F/Admin/LayoutData 'appVer'). 둘을 다 본다.
     * 예전엔 이 값을 아무도 넣지 않는 this._appVer 에서 읽어서, 어느 화면에서 열든
     * NaN 비교가 되어 '최신' 이 한 번도 뜨지 않고 늘 번호가 나왔다.
     */
    static appVer(row) {
        if (row && row.mobile_type === 'ios') return LayoutData.get('appVerIos') || 0;
        if (typeof window.appVer !== 'undefined' && window.appVer) return window.appVer;
        const layoutVer = LayoutData.get('appVer');
        if (layoutVer) return layoutVer;
        return 0;
    }

/**
     * 회원번호 클릭 → 액션 목록. $j.Ui.Tooltip 을 앵커 팝오버로 쓴다
     * (body 부착이라 DataTables 의 overflow 에 안 잘리고, 같은 번호를 다시 누르면 닫힌다).
     *
     * 예전에는 액션이 셀마다 흩어져 있었다 — 업종=정보요약, 핸드폰=앱화면, 이름=편집, 톡=불러톡,
     * 서류=심사. 어디를 눌러야 뭐가 뜨는지 외워야 했고, 목록에서 아예 닿지 못하는 것도 있었다
     * (기사눈 서류화면·캐시내역은 편집창까지 들어가야 했다).
     *
     * 앱 미설치(mobie_ver<2)면 불러톡·앱화면을 **흐리게만** 둔다. 없애면 '왜 없지' 를 묻게 된다.
     */
    static ACTIONS = {
        edit:    { label: '기사편집',        run: (n, row) => $j.F.Admin.Util.memberPortal(n) },

        // 소속자(직원)일 때만 — **소유자(사장)의 편집창**으로 건너뛴다.
        // 캐시·계좌·사업자정보는 소유자에게만 있어서, 소속자를 보다가 그리로 옮겨가는 일이 잦다.
        // 목록에서는 '소유자' 칸을 눌러 갔는데, 팝오버로 들어오면 그 길이 없었다.
        owner:   { label: '소유자 기사편집', icon: 'fas fa-pen', subOnly: true,
                   run: (n, row) => $j.F.Admin.Util.memberPortal(Util.int(row.mem_ownercode)) },
        chat:    { label: '불러톡',          icon: 'fab fa-rocketchat',        run: (n) => $j.F.Admin.Util.chat(n), needApp: true },

        cars:    { label: '장비/기사 관리',   run: (n, row) => $j.F.Admin.Member.CarDrivers.open({
                       owner: row.mem_ownercode, driverType: row.mem_driver_type }) },
        doc:     { label: '서류심사',        run: (n) => $j.F.Admin.Util.memberDocEdit(n) },
        // 기사편집 하단의 로그인기록·변경로그와 같은 창이다 (F/Admin/Member/Logs).
        // 상담 중 "언제 로그인했나·누가 바꿨나" 를 보려고 편집창까지 들어가던 것을 여기서 연다.
        loginLog:  { label: '로그인기록',      run: (n) => $j.F.Admin.Member.Logs.logins(n) },
        memberLog: { label: '변경내역',        run: (n) => $j.F.Admin.Member.Logs.changes(n) },

        // 아래는 전부 대리로그인(/m/login/proxy)으로 기사 화면을 그대로 여는 것들이다.
        // 앱 설치와 무관하게 열리므로 needApp 을 걸지 않는다 — 막는 건 불러톡뿐이다.
        // 아이콘은 '알림' 이 아니라 **모바일** 쪽에 무게를 둔다 — 이 줄이 여는 것은 알림함이 아니라
        // 기사 폰 화면(대리로그인)이고, 아래 모바일 묶음의 머리이기도 하다.
        app:     { label: '모바일 알림',      icon: 'fas fa-mobile-screen',     run: (n) => $j.F.Admin.Util.userOrder(n) },
        // 캐시는 **소유자 전유물**이라 소속자에겐 내지 않는다 — 기사편집도 같은 기준으로
        // 소속자에게 정산 묶음(회비결제·계좌·보유캐시)을 안 그린다(MemberEdit/Visibility 의 BLOCKS).
        mCash:   { label: '모바일 캐시내역',  ownerOnly: true, run: (n) => $j.F.Admin.Util.userCash(n) },
        mDoc:    { label: '모바일 서류보기',  ownerOnly: true, run: (n) => $j.F.Admin.Util.userDocs(n) },

        // 악덕알리미는 별도 앱(/b/)이다. 장비를 굴리는 **수발주자 소유자**만 쓴다 —
        // 발주자에게는 알려줄 것이 없고, 소속자는 체불을 신고·조회할 당사자가 아니다
        devil:   { label: '악덕알리미',       icon: 'fas fa-exclamation-triangle',
                   sooOnly: true, ownerOnly: true, run: (n) => $j.F.Admin.Util.devilMobile(n) },
    };

    /** 구분선 위치까지 담은 표시 순서 */
    // 'edit' 는 머리줄 아이콘이라 목록에 없다 (ACTIONS 에는 남아 있고 아이콘이 같은 run 을 쓴다)
    static ACTION_ORDER = ['owner', 'chat', 'cars', 'doc', 'loginLog', 'memberLog', '-', 'app', 'mCash', 'mDoc', 'devil'];

    /** 액션 실행 때 쓸 행 데이터 — 팝오버가 body 에 붙어 DOM 으로는 행을 못 거슬러 올라간다 */
    static _rowOf($el) {
        return this._lastRow && String(this._lastRow.num) === String($el.data('mb-no'))
            ? this._lastRow : {};
    }

    /**
     * 팝오버 위쪽 정보 요약 — 표에서 훑던 작은 칸들을 여기 모은다.
     * 값은 컬럼과 같은 헬퍼에서 나온다(msixPt/counterOf/sooCnt/cashLeft).
     * @returns {Array<{k:string,v:string}>} v 는 HTML (서버가 만든 _status 를 그대로 쓴다)
     */
    static infoRows(row) {
        // 최신이면 번호 대신 '최신' 을 쓴다 — 최신 여부만 알면 되는 자리라 번호는 읽을 것이 없다.
        // 기준을 모를 때만(latest<=0) 번호를 그대로 둔다. 0 을 기준으로 삼으면 전부 최신이 된다.
        const ver = row.mobie_ver;
        const latest = parseFloat(this.appVer(row));
        let verStr = '-';
        if (ver != null && ver !== '') {
            verStr = (latest > 0 && parseFloat(ver) >= latest)
                ? '<span style="color:var(--adm-kw-blue, blue)">최신</span>'
                : `${ver}`;
        }
        const cash = this.cashLeft(row);
        const pt   = this.msixPt(row);

        // 캐시 줄은 **소유자에게만** 낸다 — 소속자에게는 '-' 가 아니라 없는 개념이다
        // (cashLeft 가 null 을 주는 기준과 같다). 액션의 '모바일 캐시내역' 과 한 규칙이다.
        return [
            ...(cash === null ? [] : [{ k: '캐시', v: Util.formatNumber(cash) }]),
            { k: '-6차',   v: pt > 0 ? pt.toLocaleString() : '-' },
            { k: '차수',   v: String(this.counterOf(row)) },
            { k: '수주',   v: String(this.sooCnt(row)) },
            { k: '버전',   v: verStr },
            { k: '상태',   v: Util.nvl(row._status) || '-' },
            { k: '서류',   v: `${row._doc_cnt}/${row._doc_max}` },
            { k: '로그인', v: Util.nvl(row._loginDate) || '-' },
        ];
    }

    static open(anchor, row) {
        this._bindActions();
        this._injectStyle();
        this._lastRow = row;
        const n = row.num;
        const hasApp = parseFloat(row.mobie_ver) >= 2;

        // 머리줄 — 회원번호·이름 옆에 기사편집 아이콘. Tooltip 의 title 슬롯은 이스케이프되므로
        // 여기서 직접 그리고, 클릭은 아래 액션들과 같은 위임을 탄다.
        const head = `<div class="mb-head">`
            // 이름줄도 기사편집으로 간다 — 옆의 연필과 같은 자리다. 아래 액션들과 같은 위임을 타므로
            // data-act/data-mb-no 만 얹으면 된다 (연필은 그 자리를 알려주는 표지로 남긴다).
            + `<span class="mb-head__n" data-act="edit" data-mb-no="${n}"`
            + ` role="button" tabindex="0" title="기사편집">#${n} ${this._esc(Util.nvl(row.mem_name))}</span>`
            + `<button type="button" class="mb-head__edit" data-act="edit" data-mb-no="${n}"`
            + ` title="기사편집" aria-label="기사편집"><i class="fas fa-pen"></i></button>`
            + `</div>`;

        const info = `<div class="mb-info">` + this.infoRows(row)
            .map((r) => `<div class="mb-info__i"><span>${r.k}</span><b>${r.v}</b></div>`).join('')
            + `</div>`;

        const html = this.ACTION_ORDER.map((k) => {
            if (k === '-') return '<hr>';
            const a = this.ACTIONS[k];
            // sooOnly·ownerOnly·subOnly 는 해당 없으면 **줄 자체를 안 낸다** — needApp(흐리게)과
            // 다르다. 발주자에게 '악덕알리미' 를, 소속자에게 '캐시내역' 을 흐리게 보여줘 봐야
            // 알려줄 것이 없다(눌러도 열 것이 없는 게 아니라, 그 사람에게 없는 개념이다).
            if (a.sooOnly   && row.mem_driver_type !== '수발주자') return '';
            if (a.ownerOnly && row.mem_permit !== '소유자') return '';
            if (a.subOnly   && (row.mem_permit === '소유자' || !Util.int(row.mem_ownercode))) return '';
            const off = a.needApp && !hasApp;
            const sub = (k === 'doc') ? `${row._doc_cnt}/${row._doc_max}` : '';
            // 아이콘은 라벨 '안'에 넣는다 — 바깥에 두면 라벨/부가값 양끝정렬(space-between)이 어긋난다.
            // 글자 오른쪽에 둔다: 라벨 왼쪽 끝이 모든 줄에서 그대로 맞아 목록이 흔들리지 않는다.
            const lab = a.label + (a.icon ? `<i class="${a.icon} mb-act__i"></i>` : '');
            return off
                ? `<div class="jb-menu__i is-off"><span>${lab}</span><em>앱 미설치</em></div>`
                : `<div class="jb-menu__i" data-act="${k}" data-mb-no="${n}"><span>${lab}</span><em>${sub}</em></div>`;
        }).join('');

        // 누를 것들이 앵커(회원번호) 가까이 있어야 마우스가 덜 움직인다. 요약은 눈으로만 읽으니 멀어도 된다.
        // 그래서 아래로 뜰 땐 [머리줄·액션·정보], **위로 뒤집히면 앵커가 패널 아래**이므로 순서도 뒤집는다.
        // 뒤집기는 .jb-tip.is-top 을 받아 CSS 가 한다 (Ui.Tooltip 이 위치를 정하며 붙여준다).
        // tone:'panel' — 기본 툴팁의 크림+앰버는 '메모' 로 읽힌다. 이건 안내문이 아니라
        // 누를 것이 든 판이라 관리자 표·카드와 같은 바탕(--adm-surface)을 쓴다.
        $j.Ui.Tooltip.show(anchor,
            `<div class="mb-pop">${head}<div class="jb-menu mb-actions">${html}</div>${info}</div>`,
            { tone: 'panel' });
    }

    static _esc(v) {
        return String(v == null ? '' : v).replace(/[&<>"']/g, (c) =>
            ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]));
    }

    /** 액션 목록 클릭 위임 — 팝오버는 body 에 붙으므로 document 에 한 번만 건다 */
    static _bindActions() {
        if (this._actionsBound) return;
        this._actionsBound = true;
        $(document).on('click.mbact', '.jb-tip [data-act][data-mb-no]', (e) => {
            const $el = $(e.currentTarget);
            const a = this.ACTIONS[$el.data('act')];
            $j.Ui.Tooltip.hide();
            if (a) a.run(Number($el.data('mb-no')), this._rowOf($el));
        });
    }

    /** 팝오버 전용 스타일. 1회만 주입(id 가드) */
    static _injectStyle() {
        if (document.getElementById('member-pop-style')) return;
        const el = document.createElement('style');
        el.id = 'member-pop-style';
        el.textContent = `
/* 번호만 적는 자리(cellNo) — 옆 칸이 이름을 말하므로 번호는 거들 뿐이다.
   링크 파랑을 그대로 두면 표에서 번호가 가장 먼저 읽힌다. 다른 칸과 같은 글자색으로 두고,
   누를 수 있다는 것은 커서와 hover 밑줄이 말한다. */
a[data-mb-pop].mb-no{color:inherit;text-decoration:none}
a[data-mb-pop].mb-no:hover{color:inherit;text-decoration:underline}

/* 회원 액션 팝오버 — 머리줄(이름+편집) · 액션 목록 · 정보 요약(2열 격자)
   색·라운드는 전부 관리자 테마 토큰. 관리자 전용 모듈이라 폴백을 두지 않는다 —
   구분선은 껍데기(.jb-tip--panel)와 같은 --adm-border 를 써야 테두리와 한 벌로 움직인다. */
.mb-pop{display:flex;flex-direction:column}
/* 위로 뒤집힌 팝오버 — 앵커가 패널 아래에 있으므로 머리줄·액션을 아래로 내린다.
   테두리·여백은 방향이 있어 같이 뒤집어야 선이 엉뚱한 쪽에 남지 않는다. */
.jb-tip.is-top .mb-pop{flex-direction:column-reverse}
.mb-head{display:flex;align-items:center;gap:7px;
  margin:0 0 8px;padding-bottom:7px;border-bottom:1px solid var(--adm-border)}
.jb-tip.is-top .mb-head{margin:8px 0 0;padding:7px 0 0;
  border-bottom:0;border-top:1px solid var(--adm-border)}
.mb-head__n{font-weight:700;font-size:13.5px;color:var(--adm-text-heading);cursor:pointer}
.mb-head__n:hover{text-decoration:underline}
.mb-head__edit{flex:none;width:22px;height:22px;padding:0;
  border:1px solid var(--adm-border);border-radius:var(--adm-radius-sm);
  background:var(--adm-surface);color:var(--adm-text-muted);cursor:pointer;line-height:1;font-size:11px}
.mb-head__edit:hover{background:var(--adm-row-hover);color:var(--adm-text-heading)}
.mb-info{display:grid;grid-template-columns:1fr 1fr;gap:2px 14px;margin:8px 0 0;
  padding-top:8px;border-top:1px solid var(--adm-border)}
.jb-tip.is-top .mb-info{margin:0 0 8px;padding:0 0 8px;
  border-top:0;border-bottom:1px solid var(--adm-border)}
.mb-info__i{display:flex;align-items:baseline;justify-content:space-between;gap:8px;
  font-size:12px;line-height:1.7;white-space:nowrap}
.mb-info__i span{color:var(--adm-text-muted)}
.mb-info__i b{font-weight:700;color:var(--adm-text-heading)}
/* 액션 목록의 모양(줄·구분선·hover·위험색·흐리게)은 판톤 공용 .jb-menu 가 그린다
   (Ui/Tooltip.js). 여기 남는 것은 이 팝오버만의 것 — 넓이와 아이콘뿐이다. */
/* 껍데기 선택자를 함께 적는다 — 공용 규칙(.jb-tip--panel .jb-menu)이 특이도 (0,2,0) 이라
   클래스 하나로는 min-width 를 못 이긴다 */
.jb-tip--panel .mb-actions{min-width:240px}
/* 아이콘 — 라벨 오른쪽. 라벨 길이를 따라가므로 폭을 고정하지 않는다 */
.mb-actions .mb-act__i{margin-left:7px;color:var(--adm-text-faint);font-size:11px}
.mb-actions .jb-menu__i:hover .mb-act__i{color:var(--adm-text-muted)}`;
        document.head.appendChild(el);
    }

    /** 회원번호만 있을 때 — 서버에서 한 벌 받아 연다 */
    static openByNo(anchor, mbNo) {
        $.getJSON(`/adm/ajax/member/${mbNo}/pop`, (r) => {
            if (!r || !r.result) return;
            this.open(anchor, r.row);
        });
    }

    /**
     * 회원칸 한 개를 그린다 — **관리자 화면의 회원 표기 규약.**
     *
     *   $j.F.Admin.Member.Pop.cell(21429, '이재일')   →  <a data-mb-pop=21429>이재일#21429</a>
     *
     * 규약 둘.
     *   1. **번호만 덩그러니 두지 않는다.** 사람은 번호를 못 알아본다. 늘 `이름#번호` 다
     *      (검색칸이 쓰는 표기와 같다 — 그대로 복사해 검색칸에 붙일 수 있다)
     *   2. **누르면 회원 팝오버가 뜬다.** 화면마다 다른 창이 뜨면 CS 가 화면마다 다시 배운다
     *
     * 쓰는 화면은 `bind(selector)` 를 한 번 불러 위임을 걸어둔다.
     * 이름을 모르면 넘기지 않아도 된다 — 그때는 `회원#번호` 로 적힌다.
     *
     * @param {number|string} mbNo 회원번호
     * @param {string} [name] 회원명
     * @param {{sub?:string, stop?:boolean, name?:boolean}} [opts] sub: 뒤에 흐리게 덧붙일 말(지역 등)
     *        stop: **행 자체가 눌리는 표**(행 클릭 = 선택·펼치기)에서 쓴다 — 위임 대신 링크가 직접
     *        팝오버를 열고 클릭을 행으로 올려보내지 않는다. 위임(data-mb-pop)으로 두면 행 동작까지 같이 돈다.
     *        name: `#번호` 를 떼고 이름만 적는다. **바로 옆에 전화번호가 붙어** 누구인지 이미 가려지는
     *        자리에서만 쓴다(오더핸들 발주자·수주자·작업기사). 눌렀을 때 뜨는 팝오버는 같다.
     */
    static cell(mbNo, name, opts = {}) {
        this._autoBind();

        const no = Number(mbNo) || 0;
        if (!no) return '<span class="text-muted">-</span>';

        const nm  = this._esc(name) || '회원';
        const sub = opts.sub ? ` <span class="text-muted">· ${this._esc(opts.sub)}</span>` : '';
        const label = opts.name ? nm : `${nm}#${no}`;

        return `<a href="javascript:void(0)" ${this._clickAttr(no, opts)}>${label}</a>${sub}`;
    }

    /** 링크의 여는 방식 — 기본은 문서 위임(data-mb-pop), stop 이면 직접 열고 전파를 끊는다 */
    static _clickAttr(no, opts = {}) {
        return opts.stop
            ? `onclick="event.stopPropagation();$j.F.Admin.Member.Pop.openByNo(this,${no})"`
            : `data-mb-pop="${no}"`;
    }

    /**
     * **번호만** 보이는 자리 — 이름 컬럼이 따로 있는 표에서 쓴다.
     *
     * 기본은 `cell()`(`이름#번호`) 이다. 번호만 덩그러니 두지 않는다는 규약은 그대로고,
     * 여기서는 **바로 옆 칸이 이름을 말하므로** 같은 줄에 이름을 두 번 적지 않는 것뿐이다.
     * 눌렀을 때 뜨는 창은 같다.
     */
    static cellNo(mbNo, opts = {}) {
        this._autoBind();
        this._injectStyle();   // 팝오버를 열기 전에도 번호 색이 서 있어야 한다

        const no = Number(mbNo) || 0;
        if (!no) return '<span class="text-muted">-</span>';

        return `<a href="javascript:void(0)" class="mb-no" ${this._clickAttr(no, opts)}>${no}</a>`;
    }

    /**
     * 서버가 그린 `data-mb-pop` 링크도 뜨게 위임을 세운다 — 관리자 부팅(Sites/Admin/Bootstrap)이 한 번 부른다.
     * cell()/cellNo() 를 한 번도 안 부르는 화면(@foreach 로 그린 표)은 이게 없으면 눌러도 아무 일이 없다.
     */
    static autoBind() {
        this._autoBind();
        this._injectStyle();
    }

    /**
     * 문서 전체에 한 번만 거는 위임 — **화면이 bind() 를 잊어도 팝오버가 뜬다.**
     *
     * `cell()` 을 쓰는 화면이 열 곳이 넘는데 그때마다 bind() 를 따로 부르게 하면, 한 곳만
     * 빠뜨려도 눌러도 아무 일이 안 일어난다(그리고 아무도 모른다). 그래서 표기를 만드는
     * 자리에서 위임까지 같이 선다.
     *
     * 화면이 직접 건 위임(`bind(selector)`)이 있으면 그쪽이 먼저 처리하고 preventDefault 를
     * 하므로 여기서는 비켜선다 — 둘 다 열면 팝오버가 열렸다 바로 닫힌다(토글이라).
     */
    static _autoBind() {
        if (this._autoBound) return;
        this._autoBound = true;

        $(document).on('click.mbpopauto', '[data-mb-pop]', function (e) {
            if (e.isDefaultPrevented()) return;
            e.preventDefault();
            Pop.openByNo(this, Number($(this).data('mb-pop')));
        });
    }

    /**
     * 위임 — 선택자 안의 `[data-mb-pop]` 클릭에 팝오버를 붙인다.
     * 값은 회원번호. 행 데이터를 이미 들고 있는 화면은 open() 을 직접 부르는 편이 빠르다.
     */
    static bind(selector) {
        $(selector).off('click.mbpop').on('click.mbpop', '[data-mb-pop]', function (e) {
            e.preventDefault();
            Pop.openByNo(this, Number($(this).data('mb-pop')));
        });
    }

}
